import { useEffect, useRef, useState } from 'react';
import type { Asset } from '../data/campaign';
import { isBrowserAudio, isBrowserImage, isBrowserVideo } from '@brands/shared';
import { getAssetUrl } from '../lib/assetUrls';
import { formatBytes, formatDuration } from '../lib/format';

interface LightboxProps {
  campaignId: string;
  items: Asset[];
  index: number;
  notes: Map<string, string>;
  isNew: (a: Asset) => boolean;
  onIndex: (i: number) => void;
  onClose: () => void;
}

/** GIF y SVG se muestran originales (la miniatura webp no conserva animación ni vectores). */
const showOriginalImage = (a: Asset) =>
  a.mimeType === 'image/gif' || a.mimeType === 'image/svg+xml';

function useUrl(campaignId: string, asset: Asset, variant: 'large' | 'view' | null) {
  const [state, setState] = useState<{ key: string; url: string | null; failed: boolean } | null>(
    null,
  );
  const key = `${asset.id}|${variant}`;
  useEffect(() => {
    if (!variant) return;
    let alive = true;
    getAssetUrl(campaignId, asset.id, variant).then(
      (url) => alive && setState({ key, url, failed: !url }),
      () => alive && setState({ key, url: null, failed: true }),
    );
    return () => {
      alive = false;
    };
  }, [campaignId, asset.id, variant, key]);
  return state?.key === key ? state : null;
}

function NoPreview({
  asset,
  poster,
  text,
}: {
  asset: Asset;
  poster?: string | null | undefined;
  text: string;
}) {
  return (
    <div className="lightbox__file">
      {poster ? <img className="lightbox__poster" src={poster} alt="" /> : null}
      <p className="t-title">{asset.name.split('.').pop()?.toUpperCase()}</p>
      <p className="t-text">{text}</p>
    </div>
  );
}

function Media({ campaignId, asset }: { campaignId: string; asset: Asset }) {
  const kind = asset.kind;
  const [playError, setPlayError] = useState(false);
  // Fotos: original si el navegador la muestra y conviene (GIF, SVG o sin miniatura); si no, la
  // miniatura grande (HEIC, TIFF, PSD, RAW…). Video y audio: el archivo, si el navegador lo
  // reproduce; si no, su miniatura y la invitación a descargar.
  const browserImage = isBrowserImage(asset.mimeType);
  const playable =
    (kind === 'video' && isBrowserVideo(asset.mimeType)) ||
    (kind === 'audio' && isBrowserAudio(asset.mimeType));
  const mainVariant =
    kind === 'image'
      ? showOriginalImage(asset) || (browserImage && !asset.thumbLargePath)
        ? 'view'
        : asset.thumbLargePath
          ? 'large'
          : null
      : playable || kind === 'pdf'
        ? 'view'
        : null;
  const main = useUrl(campaignId, asset, mainVariant);
  const poster = useUrl(
    campaignId,
    asset,
    (kind === 'video' || kind === 'audio') && asset.thumbLargePath ? 'large' : null,
  );

  if (mainVariant && !main) return <p className="lightbox__status t-meta">Cargando…</p>;
  if (main?.failed)
    return <p className="lightbox__status t-meta">No se pudo cargar la vista previa.</p>;

  const download = 'Descárgalo para abrirlo con tu reproductor o editor.';
  if (kind === 'video') {
    if (main?.url && !playError) {
      return (
        <video
          key={asset.id}
          className="lightbox__media"
          src={main.url}
          poster={poster?.url ?? undefined}
          controls
          playsInline
          preload="metadata"
          onError={() => setPlayError(true)}
        />
      );
    }
    return (
      <NoPreview
        asset={asset}
        poster={poster?.url}
        text={`Este formato de video no se reproduce en el navegador. ${download}`}
      />
    );
  }
  if (kind === 'audio') {
    if (main?.url && !playError) {
      return (
        <div className="lightbox__audio">
          {poster?.url ? (
            <img className="lightbox__poster" src={poster.url} alt="" />
          ) : (
            <span className="lightbox__audio-icon" aria-hidden="true">
              ♪
            </span>
          )}
          <p className="t-title">{asset.name}</p>
          <audio
            key={asset.id}
            src={main.url}
            controls
            preload="metadata"
            onError={() => setPlayError(true)}
          />
        </div>
      );
    }
    return (
      <NoPreview
        asset={asset}
        text={`Este formato de audio no se reproduce en el navegador. ${download}`}
      />
    );
  }
  if (kind === 'pdf' && main?.url) {
    return (
      <div className="lightbox__pdf">
        <iframe className="lightbox__frame" src={main.url} title={asset.name} />
        <a
          className="btn btn--ghost lightbox__pdf-link"
          href={main.url}
          target="_blank"
          rel="noreferrer"
        >
          Abrir PDF
        </a>
      </div>
    );
  }
  if (main?.url) {
    return <img key={asset.id} className="lightbox__media" src={main.url} alt={asset.name} />;
  }
  return <NoPreview asset={asset} text={`Este formato no tiene vista previa. ${download}`} />;
}

export function Lightbox({
  campaignId,
  items,
  index,
  notes,
  isNew,
  onIndex,
  onClose,
}: LightboxProps) {
  const asset = items[index];
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchX = useRef<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  const go = (delta: number) => onIndex((index + delta + items.length) % items.length);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % items.length);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndex]);

  // Precarga de las miniaturas grandes vecinas para que avanzar se sienta inmediato.
  useEffect(() => {
    for (const n of [items[index + 1], items[index - 1]]) {
      if (n?.thumbLargePath) void getAssetUrl(campaignId, n.id, 'large').catch(() => undefined);
    }
  }, [campaignId, index, items]);

  if (!asset) return null;

  async function download() {
    if (!asset) return;
    setDownloading(true);
    try {
      const url = await getAssetUrl(campaignId, asset.id, 'download');
      if (url) window.location.assign(url);
    } finally {
      setDownloading(false);
    }
  }

  const meta = [
    asset.width && asset.height ? `${asset.width}×${asset.height}` : '',
    formatDuration(asset.durationMs),
    formatBytes(asset.size),
  ].filter(Boolean);
  const note = notes.get(asset.id);

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={asset.name}
      onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        const start = touchX.current;
        const end = e.changedTouches[0]?.clientX;
        touchX.current = null;
        if (start === null || end === undefined || Math.abs(end - start) < 60) return;
        go(end < start ? 1 : -1);
      }}
    >
      <div className="lightbox__bar">
        <span className="t-meta lightbox__count">
          {index + 1} / {items.length}
        </span>
        <span className="lightbox__actions">
          <button
            type="button"
            className="btn btn--on-ink"
            disabled={downloading}
            onClick={() => void download()}
          >
            {downloading ? 'Preparando…' : 'Descargar'}
          </button>
          <button
            ref={closeRef}
            type="button"
            className="lightbox__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </span>
      </div>

      <div className="lightbox__stage">
        <Media key={asset.id} campaignId={campaignId} asset={asset} />
      </div>

      {items.length > 1 ? (
        <>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={() => go(-1)}
            aria-label="Anterior"
          >
            ‹
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={() => go(1)}
            aria-label="Siguiente"
          >
            ›
          </button>
        </>
      ) : null}

      <div className="lightbox__caption">
        <p className="t-text lightbox__name">
          {isNew(asset) ? <span className="badge-new">Nuevo</span> : null} {asset.name}
        </p>
        <p className="t-meta lightbox__meta">{meta.join(' · ')}</p>
        {note ? <p className="t-text lightbox__note">{note}</p> : null}
      </div>
    </div>
  );
}
